import { forwardRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpha, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ActivityTypes, InternalNavigationScreens } from 'features/entities/entitiesTypes';
import { jbColors } from 'app/theme/jbColors';
import { updateDashboardActivityRequest } from 'features/entities/entitiesRequests';
import { screenToRoutesMap } from 'features/routes/appRoutes';
import { MigrationArrows } from 'features/migration/MigrationArrows';
import { ReactComponent as MigrationIconStart } from 'common/assets/jb_blue_card.svg';
import { ReactComponent as MigrationIconEnd } from 'common/assets/jb_one_card.svg';

type Props = {
  migrationStartCount: number;
  migrationCompleteCount: number;
};

export const MigrationInProgressServiceCard = forwardRef<HTMLLIElement, Props>(
  ({ migrationStartCount, migrationCompleteCount }, ref) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const buttonText = t('migration.tile.inProgress.button');
    const title1Text = t('migration.tile.inProgress.title1');
    const title2Text = t('migration.tile.inProgress.title2');
    const description1Text = t('migration.tile.inProgress.description1');
    const accountText = t('migration.tile.inProgress.account');
    const accountsText = t('migration.tile.inProgress.accounts');
    const description2Text = t('migration.tile.inProgress.description2');

    const migrationServiceDiff = migrationStartCount;
    const countText = ` ${migrationServiceDiff} ${migrationServiceDiff === 1 ? accountText : accountsText} `;

    const handleRedirect = useCallback(() => {
      updateDashboardActivityRequest({
        activityType: ActivityTypes.INTERNAL_NAVIGATION,
        screenName: InternalNavigationScreens.MIGRATION_START_SCREEN,
      });
      navigate(screenToRoutesMap[InternalNavigationScreens.MIGRATION_START_SCREEN]);
    }, [navigate]);

    return (
      <Box
        component="li"
        sx={{
          mb: 0.5,
        }}
        ref={ref}
      >
        <Box
          sx={{
            width: 224,
            background: (theme) =>
              `linear-gradient(99.47deg, ${theme.palette.common.white} 0%, #E4E5E6 100.72%)`,
            boxShadow: (theme) =>
              `0px 3px 5px -1px ${alpha(theme.palette.primary.main, 0.1)}, 0px 1px 18px 0px ${alpha(
                theme.palette.common.black,
                0.12
              )}, 0px 6px 10px 0px ${alpha(theme.palette.primary.main, 0.14)}`,
            borderRadius: '5px',
            p: (theme) => theme.spacing(3, 2.5, 3),
            '@media (min-width: 360px)': {
              width: 270,
            },
            '@media (min-width: 481px)': {
              width: 360,
            },
          }}
          onClick={handleRedirect}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                maxHeight: 96,
                gap: 2.5,
                '@media (min-width: 360px)': {
                  gap: 1.25,
                },
                '@media (min-width: 481px)': {
                  gap: 2.5,
                },
              }}
              className="migrationGraphics"
            >
              <Box
                sx={{
                  width: 114,
                  aspectRatio: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '@media (min-width: 360px)': {
                    width: 80,
                  },
                  '@media (min-width: 481px)': {
                    width: 114,
                  },
                }}
              >
                <MigrationIconStart
                  style={{
                    flex: '0 0 104px',
                  }}
                />
              </Box>
              <MigrationArrows
                sx={{
                  position: 'relative',
                  // the icons have some extra white space on
                  // the left because of the shadow of the svg
                  // so we move the arrows to visually align
                  left: 1,
                  top: -2,
                  fontSize: 40,
                  '@media (min-width: 360px)': {
                    width: '1.40em',
                  },
                  '@media (min-width: 481px)': {
                    width: '2em',
                  },
                }}
              />
              <Box
                sx={{
                  width: 114,
                  aspectRatio: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '@media (min-width: 360px)': {
                    width: 88,
                  },
                  '@media (min-width: 481px)': {
                    width: 114,
                  },
                }}
              >
                <MigrationIconEnd
                  style={{
                    flex: '0 0 104px',
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Box sx={{ pt: 2 }}>
            <Typography
              component="div"
              sx={{
                color: jbColors.support.royalBlue,
                fontWeight: 'fontWeightLight',
                fontSize: (theme) => theme.typography.pxToRem(16),
                lineHeight: 1.37,
                letterSpacing: '0.04rem',
                textTransform: 'uppercase',
                '@media (min-width: 360px)': {
                  fontSize: (theme) => theme.typography.pxToRem(20),
                  lineHeight: 1.3,
                },
              }}
            >
              {title1Text}
              <span style={{ fontWeight: '500' }}>{' JB ONE '}</span>
              {title2Text}
            </Typography>
            <Typography
              variant="body2"
              component="div"
              sx={{
                mt: 1,
                fontWeight: 'fontWeightRegular',
                lineHeight: 1.42,
                background: (theme) =>
                  `linear-gradient(-99.47deg, ${theme.palette.common.white} 0%, #E4E5E6 100.72%)`,
              }}
            >
              {description1Text}
              <span style={{ fontWeight: '700' }}>{countText}</span>
              {description2Text}
            </Typography>
          </Box>
          <Box sx={{ pt: 2, display: 'flex' }}>
            <Typography
              variant="body2"
              component="div"
              sx={{
                padding: '2px 5px 2px 5px',
                fontWeight: 'fontWeightBold',
                textAlign: 'center',
                border: `1px solid ${jbColors.support.royalBlue200}`,
                borderRadius: '3px',
                px: 1.5,
                py: 0.75,
              }}
            >
              {buttonText}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }
);
